import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar } from "@mui/material";
import React, { useState, createContext, useContext } from "react";

export const AlertsContext = createContext({
  success: (message: string) => {},
  error: (message: string) => {},
  warning: (message: string) => {},
  info: (message: string) => {},
  customAlert: (message: string, bg: string, color: string) => {},
  infoModal: (title: string, message: string) => {},
  confirmModal: (title: string, message: string, onConfirm: () => void) => {},
});

export enum AlertSeverity {
  SUCCESS = "success",
  ERROR = "error",
  WARNING = "warning",
  INFO = "info",
}

export function AlertsProvider(props: { children?: React.ReactNode }) {
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState(AlertSeverity.SUCCESS);
  const [customAlertProps, setCustomAlertProps] = useState({
    message: "",
    color: "",
    bg: "",
  });
  const [modalProps, setModalProps] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm?: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const success = (message: string) => {
    setAlertSeverity(AlertSeverity.SUCCESS);
    setAlertMessage(message);
  };
  const error = (message: string) => {
    setAlertSeverity(AlertSeverity.ERROR);
    setAlertMessage(message);
  };
  const warning = (message: string) => {
    setAlertSeverity(AlertSeverity.WARNING);
    setAlertMessage(message);
  };
  const info = (message: string) => {
    setAlertSeverity(AlertSeverity.INFO);
    setAlertMessage(message);
  };

  return (
    <AlertsContext.Provider
      value={{
        success: success,
        error: error,
        warning: warning,
        info: info,
        customAlert: (message: string, bg: string, color: string) => setCustomAlertProps({ message: message, bg: bg, color: color }),
        infoModal: (title, message) => setModalProps({ open: true, title: title, message: message, onConfirm: undefined }),
        confirmModal: (title, message, onConfirm) => setModalProps({ open: true, title: title, message: message, onConfirm: onConfirm }),
      }}
    >
      <>
        {props.children}
        <Dialog open={modalProps.open} onClose={() => setModalProps({ ...modalProps, open: false })}>
          <DialogTitle>{modalProps.title}</DialogTitle>
          <DialogContent>{modalProps.message}</DialogContent>
          <DialogActions>
            <Button color="secondary" onClick={() => setModalProps({ ...modalProps, open: false })}>
              {modalProps.onConfirm ? "Ne" : "Zavřít"}
            </Button>
            {modalProps.onConfirm && (
              <Button
                color="primary"
                onClick={() => {
                  modalProps.onConfirm?.();
                  setModalProps({ ...modalProps, open: false });
                }}
              >
                Ano
              </Button>
            )}
          </DialogActions>
        </Dialog>
        <Snackbar open={Boolean(alertMessage)} onClose={() => setAlertMessage("")} autoHideDuration={6000} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
          <Alert onClose={() => setAlertMessage("")} severity={alertSeverity}>
            {alertMessage}
          </Alert>
        </Snackbar>
        <Snackbar open={Boolean(customAlertProps.message)} onClose={() => setCustomAlertProps({ ...customAlertProps, message: "" })} autoHideDuration={6000} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
          <Alert style={{ color: customAlertProps.color, backgroundColor: customAlertProps.bg }} onClose={() => setCustomAlertProps({ ...customAlertProps, message: "" })} icon={false}>
            {customAlertProps.message}
          </Alert>
        </Snackbar>
      </>
    </AlertsContext.Provider>
  );
}

export const useAlerts = () => useContext(AlertsContext);
